import React, { useState, useEffect } from "react";
import api from "../api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/EnterpriseDetails.css";
import { formatDateToUserTimeZone } from "../utils/formatDate";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import crossIcon from "../assets/icons/crossIcon.png";
import editIcon from "../assets/Images/Edit.png";
import viewIcon from "../assets/Images/View.png";
import changeStatusIcon from "../assets/Images/ChangeStatus.png";
import addIcon from "../assets/Images/AddIcon.png";

import downloadIcon from "../assets/Images/Download.png";
import filterIcon from "../assets/Images/Filter.png";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useDispatch } from "react-redux";
import { setRefreshSidebar } from "../redux/sidebarSlice";

import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import modelsIcon from "../assets/icons/modulesvg.png";
import defaultIcon from "../assets/ProfileSettingIcons/default_module_icon.svg";
import { useTableSort } from "../hooks/useTableSort";
import { SortableHeader } from "../components/SortableHeader";

const BASE_URL = `${process.env.REACT_APP_API_URL}/enterprise-api-keys`;

const ApiManagement = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    provider_name: "",
    api_key: "",
    module_icon: null,
  });
  const [viewData, setViewData] = useState(null);
  const [editingApiKeyId, setEditingApiKeyId] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const MySwal = withReactContent(Swal);
  const dispatch = useDispatch();

  const [apiKeyCounts, setApiKeyCounts] = useState({
    totalApiKeys: 0,
    activeApiKeys: 0,
    inactiveApiKeys: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchApiKeyCounts = async () => {
    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");
    const res = await api.get(`${BASE_URL}/status-summary`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    // map backend snake_case keys to camelCase
    setApiKeyCounts({
      totalApiKeys: res.data.total_api_keys,
      activeApiKeys: res.data.active_api_keys,
      inactiveApiKeys: res.data.inactive_api_keys,
    });
  };

  useEffect(() => {
    fetchApiKeyCounts();
  }, []);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const fetchApiKeys = async () => {
    if (!token) return showErrorToast(TOAST_MESSAGES.UNAUTHORIZED);

    try {
      const response = await api.get(`${BASE_URL}/enterprise-details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApiKeys(response.data);
    } catch (error) {
      console.error("Failed to fetch API keys", error);
      showErrorToast(TOAST_MESSAGES.API_KEY.FETCH_ERROR);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const handleReset = () => {
    setSortOrder("asc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleApply = () => {
    // console.log("Sorting by:", sortOrder);
    // console.log("Filtering by day:", dayFilter);
  };

  const filteredApiKeys = apiKeys
    .filter((apiKey) => {
      const lower = searchTerm.toLowerCase();
      const name = apiKey.provider_name
        ? apiKey.provider_name.toLowerCase()
        : "";
      const desc = apiKey.api_key ? apiKey.api_key.toLowerCase() : "";
      const matchesSearch = name.includes(lower) || desc.includes(lower);

      if (!matchesSearch) return false;

      if (dayFilter === "Today" || dayFilter === "Yesterday") {
        const now = new Date();
        const apiKeyDate = new Date(apiKey.updated_timestamp);
        const today = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
        );
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);

        if (dayFilter === "Today") {
          return apiKeyDate >= today;
        } else {
          return apiKeyDate >= yesterday && apiKeyDate < today;
        }
      }

      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.updated_timestamp);
      const dateB = new Date(b.updated_timestamp);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  const totalEntries = filteredApiKeys.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredApiKeys.slice(indexOfFirst, indexOfLast);
  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = Math.min(indexOfLast, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const { sortedData, requestSort, sortConfig } = useTableSort(currentData);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const handleChangeStatus = async (apiKey) => {
    const currentStatus = apiKey.enterprise_api_key_status; // "1" = Active, "0" = Inactive
    const isCurrentlyActive = currentStatus === "1";
    const newStatus = isCurrentlyActive ? "0" : "1";
    const statusText = isCurrentlyActive ? "deactivated" : "activated";

    try {
      const res = await fetch(
        `${BASE_URL}/${apiKey.enterprise_api_key_setting_id}/status`,
        {
          method: "PATCH",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!res.ok) throw new Error("Failed to change status");

      await fetchApiKeys();
      dispatch(setRefreshSidebar(true));
      fetchApiKeyCounts();

      showSuccessToast(`API Key ${statusText} successfully.`);
    } catch (err) {
      console.error(err);
      showErrorToast(TOAST_MESSAGES.API_KEY.STATUS_CHANGE_ERROR);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this API key?"))
      return;
    try {
      const res = await fetch(`${BASE_URL}/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Delete failed");
      await fetchApiKeys();
      if (currentData.length === 1 && currentPage > 1)
        setCurrentPage(currentPage - 1);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (apiKey) => {
    setFormData({
      provider_name: apiKey.provider_name,
      api_key: apiKey.api_key || "",
      module_icon: null,
      existingIconName: apiKey.module_icon
        ? apiKey.module_icon.split("/").pop()
        : "",
    });

    setEditingApiKeyId(apiKey.enterprise_api_key_setting_id);
    setShowForm(true);
  };

  const handleView = (apiKey) => {
    setViewData(apiKey);
    setFormData({
      provider_name: apiKey.provider_name,
      api_key: apiKey.api_key || "",
      module_icon: null,
      existingIconName: apiKey.module_icon || "",
    });
    setShowForm(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formPayload = new FormData();
      formPayload.append("provider_name", formData.provider_name);
      formPayload.append("api_key", formData.api_key);
      if (formData.module_icon) {
        formPayload.append("icon", formData.module_icon);
      }

      let res;
      if (editingApiKeyId) {
        res = await fetch(`${BASE_URL}/${editingApiKeyId}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
          body: formPayload,
        });
      } else {
        res = await fetch(BASE_URL, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formPayload,
        });
      }

      const data = await res.json();

      // 👇 Handle duplicate API key name error
      if (!res.ok) {
        if (
          data.message &&
          data.message.toLowerCase().includes("already exists")
        ) {
          showErrorToast(TOAST_MESSAGES.API_KEY.DUPLICATE_NAME_ERROR);
        } else {
          showErrorToast(
            editingApiKeyId
              ? TOAST_MESSAGES.API_KEY.UPDATE_ERROR
              : TOAST_MESSAGES.API_KEY.CREATE_ERROR,
          );
        }
        return;
      }

      // ✅ Success flow
      setFormData({ provider_name: "", api_key: "", icon: null });
      setShowForm(false);
      setEditingApiKeyId(null);
      setViewData(null);
      await fetchApiKeys();
      dispatch(setRefreshSidebar(true));

      showSuccessToast(
        editingApiKeyId
          ? TOAST_MESSAGES.API_KEY.UPDATE_SUCCESS
          : TOAST_MESSAGES.API_KEY.CREATE_SUCCESS,
      );
    } catch (err) {
      console.error("Error in handleFormSubmit:", err);
      showErrorToast(TOAST_MESSAGES.API_KEY.GENERAL_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowForm(false);
    setFormData({ provider_name: "", api_key: "", icon: null });
    setEditingApiKeyId(null);
    setViewData(null);
  };

  const exportExcel = () => {
    const wsData = [
      ["Provider", "API Key", "Status"],
      ...filteredApiKeys.map((m) => [
        m.provider_name,
        m.api_key || "",
        m.enterprise_api_key_status === "1" ? "Active" : "Inactive",
        formatDateToUserTimeZone(m.created_timestamp),
        formatDateToUserTimeZone(m.updated_timestamp),
        m.created_by || "",
        m.updated_by || "",
      ]),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "API Keys");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      "api_key_management.xlsx",
    );
  };

  const statsData = [
    {
      label: "Total Apis",
      value: apiKeyCounts.totalApiKeys || 0,
      icon: modelsIcon,
      summary: [],
    },
    {
      label: "Active Apis",
      value: apiKeyCounts.activeApiKeys || 0,
      icon: modelsIcon,
      summary: [],
    },
    {
      label: "Inactive Apis",
      value: apiKeyCounts.inactiveApiKeys || 0,
      icon: modelsIcon,
      summary: [],
    },
  ];

  return (
    <div className="user-management-page mx-2">
      <div className="user-management-header">
        {/* Stats Boxes */}
        <div className="d-flex flex-wrap gap-2 mb-0 align-items-stretch">
          {statsData.map((item, index) => (
            <div
              key={index}
              className="dashboard-box p-3 gap-1 rounded shadow-sm d-flex flex-column justify-content-between"
            >
              <div className="d-flex align-items-center m-0">
                {item.icon && (
                  <img
                    src={item.icon}
                    alt={item.label}
                    style={{ width: "21px", height: "21px" }}
                    className="icon-image1"
                  />
                )}
                <h6 className="ms-2 mb-0">{item.label}</h6>
              </div>
              <h2 className="fw-bold m-0">
                {typeof item.value !== "undefined" ? item.value : "0"}
              </h2>
              {/* <div className="box-summary">
                {item.summary && item.summary.length > 0 ? (
                  item.summary.map((s, i) => (
                    <span key={i}>
                      {s.label}: {s.value}
                    </span>
                  ))
                ) : (
                  <span className="invisible">placeholder</span>
                )}
              </div> */}
            </div>
          ))}
        </div>

        <div className="d-flex justify-content-between align-items-center mt-2">
          <span className="fw-bold refresh-prompts ak-text-lg">
            Api Management Details
          </span>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-2">
          <div className="d-flex align-items-center gap-2">
            <select
              className="form-select w-auto"
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(+e.target.value);
                setCurrentPage(1);
              }}
            >
              {[50, 100, 500].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <label className="m-0 align-self-center">Entries per page</label>
          </div>

          <div className="d-flex align-items-center gap-2">
            <div className="custom-search-wrapper ">
              <div className="input-group custom-search-group gap-2">
                <span className="input-group-text p-0 ps-2">
                  <i className="bi bi-search" style={{ fontSize: "16px" }}></i>
                </span>
                <input
                  type="text"
                  className="form-control p-0 px-1"
                  placeholder="Search Modules"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>

            {/* <button
            className="btn btn-outline-secondary me-2 action-button"
            onClick={exportExcel}
            title="Export to Excel"
          >
            <img src={downloadIcon} alt="Download" />
          </button> */}

            <div>
              <button
                className="icon-button filter-dropdown-toggle action-button m-0"
                data-bs-toggle="dropdown"
                title="Filter"
              >
                <img src={filterIcon} className="icon-image" alt="Filter" />
              </button>
              <div
                className="dropdown-menu profile-menu-dropdown p-2"
                style={{ minWidth: "200px" }}
              >
                <h6 className="fw-bold refresh-prompts">Time</h6>
                <hr className="mt-1" />
                {["asc", "desc"].map((order) => (
                  <div className="form-check" key={order}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="sortOrder"
                      id={order}
                      value={order}
                      checked={sortOrder === order}
                      onChange={() => setSortOrder(order)}
                    />
                    <label className="form-check-label" htmlFor={order}>
                      {order === "asc" ? "Ascending" : "Descending"}
                    </label>
                  </div>
                ))}

                <hr className="my-2" />
                <h6 className="fw-bold refresh-prompts">By Day</h6>
                <hr className="mt-1" />
                {["Today", "Yesterday", ""].map((day) => (
                  <div className="form-check" key={day || "all"}>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="dayFilter"
                      id={day || "all"}
                      value={day}
                      checked={dayFilter === day}
                      onChange={() => setDayFilter(day)}
                    />
                    <label className="form-check-label" htmlFor={day || "all"}>
                      {day || "All"}
                    </label>
                  </div>
                ))}
                <hr className="mt-1" />
                <div className="d-flex justify-content-between mt-3">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                  <button
                    className="btn bg-dark text-white btn-sm"
                    onClick={handleApply}
                  >
                    Apply Now
                  </button>
                </div>
              </div>
            </div>

            {/* <button
            className="btn btn-outline-secondary action-button text-dark"
            onClick={() => setShowForm(true)}
            title="Add Module"
          >
            <img src={addIcon} className="icon-image" alt="Add" />
          </button> */}
          </div>
        </div>
      </div>
      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="refresh-prompts ak-text-md">No entries found.</p>
        </div>
      ) : (
        <div className="user-management-table-panel mt-2">
          <div className="table-responsive custom-ak-scollbar">
            <table className="table table-sm w-100 m-0">
              <thead>
                <tr>
                  <SortableHeader
                    column="provider_name"
                    label="Provider"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="enterprise_name"
                    label="Enterprise"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="api_key"
                    label="API Key"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="enterprise_api_key_status"
                    label="Status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="updated_timestamp"
                    label="Last Updated"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  {/* <th>Action</th> */}
                </tr>
              </thead>

              <tbody>
                {sortedData.map((m) => (
                  <tr key={m.enterprise_api_key_setting_id}>
                    {/* Provider */}
                    <td className="d-flex align-items-center gap-2">
                      {m.provider_name}
                    </td>

                    {/* Enterprise Name */}
                    <td title={m.enterprise_name}>
                      {m.enterprise_name || "—"}
                    </td>

                    {/* API Key (masked) */}
                    <td title={m.api_key}>
                      {m.api_key
                        ? `${m.api_key.slice(0, 4)}*******************************************************${m.api_key.slice(-4)}`
                        : "—"}
                    </td>

                    {/* Status */}
                    <td>
                      {m.enterprise_api_key_status === "1"
                        ? "Active"
                        : "Inactive"}
                    </td>

                    {/* Last Updated */}
                    <td>{formatDateToUserTimeZone(m.updated_timestamp)}</td>

                    {/* Actions */}
                    {/* <td>
                      <button
                        className="icon-button"
                        onClick={() => handleView(m)}
                        title="View"
                      >
                        <img
                          src={viewIcon}
                          className="icon-image"
                          alt="View"
                          style={{ height: "16px", width: "18px" }}
                        />
                      </button>

                      <button
                        className="icon-button"
                        onClick={() => handleEdit(m)}
                        title="Edit"
                      >
                        <img src={editIcon} className="icon-image" alt="Edit" />
                      </button>

                      <button
                        className="icon-button"
                        onClick={() => handleChangeStatus(m)}
                        title="Change Status"
                      >
                        <img
                          src={changeStatusIcon}
                          className="icon-image"
                          alt="Change Status"
                        />
                      </button>
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-footer bgcolor d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <nav>
              <ul className="pagination mb-0 gap-2">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(1)}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {pageNumbers.map((num) => (
                  <li
                    key={num}
                    className={`page-item ${
                      num === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageClick(num)}
                    >
                      {num}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(totalPages)}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}

      {(showForm || viewData) && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content w-50 mx-auto">
              <form onSubmit={handleFormSubmit}>
                <div className="modal-header  justify-content-center position-relative">
                  <h5 className="modal-title m-0">
                    {viewData
                      ? "View Module"
                      : editingApiKeyId
                        ? "Edit Module"
                        : "Add Module"}
                  </h5>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={closeModal}
                    aria-label="Close"
                  >
                    <img src={crossIcon} className="icon-image" alt="Close" />
                  </button>
                </div>
                <div className="modal-body">
                  <div className="mb-3 d-flex align-items-center">
                    <div className="me-2">
                      {/* 👇 Show label only properly aligned for view mode */}
                      <label
                        className={`form-label ${
                          viewData ? "d-block text-center w-100 mb-2" : "ms-4"
                        }`}
                      >
                        Icon
                      </label>
                      <div className="d-flex align-items-center">
                        {formData.existingIconName ? (
                          <img
                            src={`${
                              process.env.REACT_APP_SERVER_URL
                            }/${formData.existingIconName.replace(/\\/g, "/")}`}
                            alt="Icon Preview"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = ""; // fallback to initials if image fails
                            }}
                            style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "50%",
                              objectFit: "cover",
                              border: "1px solid #ccc",
                              marginRight: "8px",
                            }}
                            className=""
                          />
                        ) : (
                          <div
                            className="circle-avatar"
                            style={{
                              marginRight: "8px",
                              width: "40px",
                              height: "40px",
                            }}
                          >
                            {formData.provider_name
                              ?.trim()
                              .charAt(0)
                              .toUpperCase() || "?"}
                          </div>
                        )}

                        {/* Show file upload only for Add/Edit, not View */}
                        {!viewData && (
                          <input
                            type="file"
                            className="file-input-reverse"
                            accept="image/*"
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                module_icon: e.target.files[0],
                              })
                            }
                          />
                        )}
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <label className="form-label required-label">
                        Module Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.provider_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            provider_name: e.target.value,
                          })
                        }
                        required
                        onInvalid={(e) =>
                          e.target.setCustomValidity("Module name is required.")
                        }
                        onInput={(e) => e.target.setCustomValidity("")} // clears the message when user types
                        disabled={!!viewData}
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label required-label">
                      Description
                    </label>
                    <textarea
                      className="input-textarea1 p-1"
                      value={formData.api_key}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          api_key: e.target.value,
                        })
                      }
                      required
                      onInvalid={(e) =>
                        e.target.setCustomValidity(
                          "Description cannot be empty.",
                        )
                      }
                      onInput={(e) => e.target.setCustomValidity("")}
                      disabled={!!viewData}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  {viewData ? (
                    <button
                      type="button"
                      className="cancle-btn"
                      onClick={closeModal}
                    >
                      Close
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="px-5 rounded custom-btn py-2 fw-bold"
                      disabled={isSubmitting} // prevent double submit
                    >
                      {isSubmitting
                        ? editingApiKeyId
                          ? "Updating..."
                          : "Adding..."
                        : editingApiKeyId
                          ? "Update Module"
                          : "Add Module"}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiManagement;
